type SizedImageSource =
  | ImageBitmap
  | (HTMLImageElement & { width: number; height: number })

function getSourceDimensions(source: SizedImageSource): { width: number; height: number } {
  if (source instanceof ImageBitmap) {
    return { width: source.width, height: source.height }
  }
  // HTMLImageElement
  return { width: source.naturalWidth || source.width, height: source.naturalHeight || source.height }
}

function drawPaddedSquare(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  srcW: number,
  srcH: number,
  size: number
) {
  // Pad to square with transparent background, then downscale to size×size.
  // Example: 432×260 → pad to 432×432 (transparent top/bottom) → downscale.
  const maxSide = Math.max(srcW, srcH)
  const scale = size / maxSide
  const drawW = srcW * scale
  const drawH = srcH * scale
  const dx = (size - drawW) / 2
  const dy = (size - drawH) / 2

  ctx.clearRect(0, 0, size, size)
  ctx.drawImage(source, dx, dy, drawW, drawH)
}

async function fileToImageSource(fileOrBlob: Blob): Promise<SizedImageSource> {
  // Prefer createImageBitmap when available (fast, no need for FileReader).
  // Fall back to HTMLImageElement for compatibility.
  try {
    return await createImageBitmap(fileOrBlob)
  } catch {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => reject(new Error('Failed to load image'))
      img.src = URL.createObjectURL(fileOrBlob)
    })
  }
}

export async function imageBlobToSquarePngDataUrl(blob: Blob, size = 256): Promise<string> {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Canvas is not supported in this browser')
  }
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'

  const source = await fileToImageSource(blob)
  const { width: srcW, height: srcH } = getSourceDimensions(source)

  drawPaddedSquare(ctx, source as unknown as CanvasImageSource, srcW, srcH, size)

  // Clean up when we used the fallback path (object URL)
  if (source instanceof HTMLImageElement) {
    URL.revokeObjectURL(source.src)
  }

  return canvas.toDataURL('image/png')
}

export async function imageFileToSquarePngDataUrl(file: File, size = 256): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Please select an image file')
  }
  return imageBlobToSquarePngDataUrl(file, size)
}

export async function imageUrlToSquarePngDataUrl(url: string, size = 256): Promise<string> {
  // Note: this requires the image URL to be fetchable from the browser (CORS).
  const res = await fetch(url)
  if (!res.ok) {
    throw new Error(`Failed to fetch image (${res.status})`)
  }
  const blob = await res.blob()
  return imageBlobToSquarePngDataUrl(blob, size)
}


